import React from 'react';
import { Box, Flex, SimpleGrid, Text } from '@chakra-ui/react';
import { motion } from 'framer-motion';

import type { IContaCilMesStatus, MesStatus } from '../../../../../hooks/useContaCilDashboard';

const MES_ABBR = [
  'JAN',
  'FEV',
  'MAR',
  'ABR',
  'MAI',
  'JUN',
  'JUL',
  'AGO',
  'SET',
  'OUT',
  'NOV',
  'DEZ',
];

const STATUS_DOT: Record<MesStatus, string> = {
  vazio: '#94A3B8',
  parcial: '#D9A441',
  concluido: '#10B981',
  cliente: '#6366F1',
};
const STATUS_LABEL: Record<MesStatus, string> = {
  vazio: 'Sem movimento',
  parcial: 'Parcial',
  concluido: 'Concluído',
  cliente: 'Recebido do cliente',
};

const formatNumber = (n: number): string =>
  new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(n);

interface MonthBlockProps {
  mes: IContaCilMesStatus;
  selected: boolean;
  onSelect: (mes: number) => void;
  index: number;
}

// Célula flat de mês (alinhada ao design + ao calendário do dashboard):
// sigla + bolinha de status, extratos importados, nº de empresas, barra de conciliação.
const MonthBlock = ({ mes, selected, onSelect, index }: MonthBlockProps) => {
  const dot = STATUS_DOT[mes.status];
  const pct = mes.total > 0 ? (mes.conciliados / mes.total) * 100 : 0;
  const vazio = mes.total === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.035 * index, ease: [0.4, 0, 0.2, 1] }}
    >
      <Box
        as="button"
        onClick={() => onSelect(mes.mes)}
        w="full"
        textAlign="left"
        p={3}
        borderRadius="14px"
        bg={selected ? 'rgba(99,102,241,0.06)' : 'rgba(15,23,42,0.02)'}
        borderWidth="1.5px"
        borderColor={selected ? '#6366F1' : 'transparent'}
        opacity={vazio ? 0.7 : 1}
        transition="all 0.15s"
        _hover={{ bg: 'rgba(99,102,241,0.05)' }}
      >
        <Flex align="center" justify="space-between" mb={1.5}>
          <Text fontSize="11px" fontWeight="700" color="#64748B" letterSpacing="0.06em">
            {MES_ABBR[mes.mes - 1]}
          </Text>
          <Box w="8px" h="8px" borderRadius="full" bg={dot} />
        </Flex>
        <Text
          fontSize="22px"
          fontWeight="700"
          color="#0F172A"
          lineHeight="1"
          sx={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {vazio ? '·' : formatNumber(mes.total)}
        </Text>
        <Text fontSize="10px" color="#94A3B8" mb={2} mt={0.5}>
          {vazio
            ? 'sem movimento'
            : `${formatNumber(mes.empresas)} ${mes.empresas === 1 ? 'empresa' : 'empresas'}`}
        </Text>
        <Box h="5px" borderRadius="full" bg="rgba(15,23,42,0.06)" overflow="hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${pct}%` }}
            transition={{ duration: 0.5, delay: 0.05 * index, ease: [0.4, 0, 0.2, 1] }}
            style={{ height: '100%', background: '#10B981', borderRadius: '9999px' }}
          />
        </Box>
      </Box>
    </motion.div>
  );
};

interface MonthVolumeGridProps {
  meses: IContaCilMesStatus[];
  selecionado: number | null;
  onSelect: (mes: number | null) => void;
}

export const MonthVolumeGrid = ({ meses, selecionado, onSelect }: MonthVolumeGridProps) => (
  <Box>
    <SimpleGrid columns={{ base: 3, md: 4, lg: 6 }} spacing={{ base: 2.5, md: 3 }}>
      {meses.map((mes, idx) => (
        <MonthBlock
          key={mes.mes}
          mes={mes}
          selected={selecionado === mes.mes}
          onSelect={m => onSelect(selecionado === m ? null : m)}
          index={idx}
        />
      ))}
    </SimpleGrid>

    <Flex
      wrap="wrap"
      gap={4}
      justify="center"
      mt={5}
      pt={4}
      borderTopWidth="1px"
      borderColor="rgba(15, 23, 42, 0.06)"
    >
      {(Object.keys(STATUS_DOT) as MesStatus[]).map(st => (
        <Flex key={st} align="center" gap={2}>
          <Box w="8px" h="8px" borderRadius="full" bg={STATUS_DOT[st]} />
          <Text fontSize="11px" color="#64748B" fontWeight="500">
            {STATUS_LABEL[st]}
          </Text>
        </Flex>
      ))}
    </Flex>
  </Box>
);
